import {Active, DragOverEvent, DragStartEvent, Over} from '@dnd-kit/core';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {VirtuosoHandle} from 'react-virtuoso';

import {PanelDropRef, SectionWithRef} from '../../types';
import {getIsReorderingSections} from '../utils';
import {logDragVital} from './logDragVital';
import {
  useHandlePanelDragEnd,
  useHandleSectionDragEnd,
} from './useHandleDragEnd';
import {useHandleDragOver} from './useHandleDragOver';
import {handleSectionDragStart} from './useHandleDragStart';

export const useHandleDrag = ({
  visibleSections,
  virtuosoRef,
  visibleRangeRef,
}: {
  visibleSections: SectionWithRef[];
  virtuosoRef: React.RefObject<VirtuosoHandle>;
  visibleRangeRef: React.RefObject<{
    startIndex: number;
    endIndex: number;
  } | null>;
}) => {
  // We need to keep track of the active item because the drag events lose track of it when dragging and scrolling simultaneously.
  // This seems to be an issue with dragging + scroll + virtualized list, so this is the workaround.
  // https://github.com/clauderic/dnd-kit/issues/794 and https://github.com/clauderic/dnd-kit/issues/832
  // This needs to be a state so we can render the drag overlay properly.
  const [activeItem, setActiveItem] = useState<Active | null>(null);
  // We intentionally avoid tracking the over item as a state to prevent unnecessary re-renders. We only need the
  // over item to determine final position of drop position when drag ends. Similarly, this is a workaround since
  // the drag event loses track of the over item when scroll occurs in a virtualized list while dragging.
  const overRef = useRef<Over | null>(null);

  const [shouldDisplaySectionContent, setShouldDisplaySectionContent] =
    useState(true);

  // Track animation frame IDs for cleanup
  const scrollAnimationFrameRef = useRef<number | null>(null);
  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (scrollAnimationFrameRef.current !== null) {
        cancelAnimationFrame(scrollAnimationFrameRef.current);
      }
    };
  }, [scrollAnimationFrameRef]);

  // We intentionally use useRef here to avoid re-rendering
  // panelDropIndicatorRef keeps track of the styles needed to render the vertical bar
  // when a panel is dragged over a panel in a different section
  const panelDropIndicatorRef = useRef<HTMLDivElement>(null);
  // panelDropRef keeps track of the over data when a panel is dragged over a panel in a
  // different section. we need granular control to insert properly
  const panelDropRef = useRef<PanelDropRef | null>(null);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      logDragVital('drag_start', {
        activeDragType: event.active.data.current?.type,
        alwaysLog: true,
      });

      setActiveItem(event.active);
      if (getIsReorderingSections(event.active)) {
        // When reordering sections, hide all the section content so it's easier
        // for users to drag sections around.
        setShouldDisplaySectionContent(false);

        handleSectionDragStart({
          active: event.active,
          visibleSections,
          virtuosoRef,
          scrollAnimationFrameRef,
          visibleRangeRef,
        });
      }
    },
    [virtuosoRef, visibleRangeRef, visibleSections]
  );

  // This is used to determine if a panel is being dragged over a panel and the drop indicator
  // vertical bar should be visible
  const onHandleDragOver = useHandleDragOver({
    panelDropIndicatorRef,
    panelDropRef,
  });
  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      if (
        event.over?.data.current &&
        Object.keys(event.over.data.current).length > 0
      ) {
        overRef.current = event.over;
      }

      onHandleDragOver(activeItem, overRef.current);
    },
    [activeItem, onHandleDragOver]
  );

  const handleSectionDragEnd = useHandleSectionDragEnd({
    visibleSections,
    virtuosoRef,
  });

  const handlePanelDragEnd = useHandlePanelDragEnd({
    panelDropRef,
  });

  const handleDragEnd = useCallback(() => {
    logDragVital('drag_end', {
      activeDragType: activeItem?.data.current?.type,
      alwaysLog: true,
    });

    setShouldDisplaySectionContent(true);

    // Hide the panel cross section drop indicator
    if (panelDropIndicatorRef.current) {
      panelDropIndicatorRef.current.style.opacity = '0';
    }

    const over = overRef.current;
    const active = activeItem;

    // Only proceed if we have both active and over, and they're different
    if (!over || !active || active.id === over.id) {
      setActiveItem(null);
      return;
    }

    const dragType = active.data.current?.type;

    if (dragType === 'section') {
      handleSectionDragEnd(active, over);
    }

    if (dragType === 'panel') {
      handlePanelDragEnd(active, over);
    }

    setActiveItem(null);
  }, [activeItem, overRef, handleSectionDragEnd, handlePanelDragEnd]);

  const handleDragReset = useCallback(() => {
    // Reset all drag-related state
    setShouldDisplaySectionContent(true);
    setActiveItem(null);

    // Hide the panel cross section drop indicator
    if (panelDropIndicatorRef.current) {
      panelDropIndicatorRef.current.style.opacity = '0';
    }

    // Cancel any pending scroll animation
    if (scrollAnimationFrameRef.current !== null) {
      cancelAnimationFrame(scrollAnimationFrameRef.current);
      scrollAnimationFrameRef.current = null;
    }
  }, [scrollAnimationFrameRef, setActiveItem, setShouldDisplaySectionContent]);

  return useMemo(() => {
    return {
      activeItem,
      handleDragEnd,
      handleDragOver,
      handleDragReset,
      handleDragStart,
      panelDropIndicatorRef,
      shouldDisplaySectionContent,
    };
  }, [
    activeItem,
    handleDragEnd,
    handleDragOver,
    handleDragReset,
    handleDragStart,
    shouldDisplaySectionContent,
  ]);
};
